import {
  IFormReportInputs,
  PayloadAnexos,
  ReportType,
  ServiceReport,
} from "@app/types/report.types";
import { normalizeDate } from "@app/utils/functions";
import { configButtonsConditioning } from "./constants";
import dayjs from "dayjs";
import { yesNoTable } from "@app/utils/tables/shared.tables";

export function filter(
  filters: IFormReportInputs,
  samplesList: ServiceReport[]
) {
  const { emission_date, emission_date_end, type } = filters;

  return samplesList.filter((sample) => {
    const sampleEmissionDate = normalizeDate(new Date(sample.emission_date));

    const filterEmissionDateStart = emission_date
      ? normalizeDate(new Date(emission_date))
      : null;
    const filterEmissionDateEnd = emission_date_end
      ? normalizeDate(new Date(emission_date_end))
      : null;

    const dateInRange =
      (!filterEmissionDateStart ||
        sampleEmissionDate >= filterEmissionDateStart) &&
      (!filterEmissionDateEnd || sampleEmissionDate <= filterEmissionDateEnd);

    const sampleReference =
      sample.results_consecutive !== null
        ? `${sample.ref_report}/${sample.results_consecutive}`
        : sample.ref_report;

    return (
      (!filters.ref_report ||
        sampleReference === filters.ref_report ||
        sample.ref_report === filters.ref_report) &&
      (!filters.client ||
        (sample.client !== null && sample.client.id === filters.client.id)) &&
      (!filters.installation ||
        sample.installation.id === filters.installation?.id) &&
      (!filters.motivo_muestreo_id ||
        sample.motivo_muestreo_id?.id === filters.motivo_muestreo_id?.id) &&
      (!type || sample.type === type.id) &&
      dateInRange
    );
  });
}

type LabelValue = {
  label: string;
  value: string | number;
};

export const mapToLabelValue = <T>(
  data: T[],
  labelPath: string,
  valuePath: string
): LabelValue[] => {
  const getValue = (obj: any, path: string) =>
    path.split(".").reduce((acc, part) => acc && acc[part], obj);

  return data.map((item) => ({
    label: getValue(item, labelPath),
    value: getValue(item, valuePath),
  }));
};

export const samplingReport = (report: any) => {
  const hasLetter = report?.anexos?.includes("letter");
  return {
    id: report?.id,
    anexo_1: report?.anexos?.includes("anexo_1"),
    anexo_2: report?.anexos?.includes("anexo_2"),
    anexo_3: report?.anexos?.includes("anexo_3"),
    acumulacion: typeof report?.acumulacion === 'number' 
      ? report?.acumulacion 
      : report?.acumulacion?.id || null,
    approved_by: report?.approved_by.id,
    client: report?.client.id,
    client_reference: report?.client_reference,
    end_date_origin_year: report?.anexos?.includes("anexo_3")
      ? report?.end_date_origin_year
      : null,
    start_date_origin_year: report?.anexos?.includes("anexo_3")
      ? report?.start_date_origin_year
      : null,
    destinatario_id: report?.destinatario_id.id,
    emission_date: report?.emission_date,
    limit_date: report?.anexos?.includes("anexo_2") ? report?.limit_date : null,
    installation: report?.report_installation?.id,
    evo_installation: report?.evo_installation?.id,
    letter: hasLetter,
    letter_content: hasLetter && report.letter_content,
    masl_reference: report?.masl_reference,
    motivo_muestreo_id: report?.motivo_muestreo_id?.id,
    punto_muestreo_id: report?.punto_muestreo_id?.id,
    report_lid: report?.report_lid === "report_lid",
    shared: report?.shared?.label,
    start_date: report?.anexos?.includes("anexo_2") ? report?.start_date : null,
    subtitle: report?.subtitle,
    tipo_muestra_id: report?.tipo_muestra_id?.id,
    tritium_water: true,
    comments: report?.comments,
    type: report?.reportType,
    reporte_tipos_analisis: report?.reporte_tipos_analisis,
    evo_start_date: report?.evo_start_date ? report?.evo_start_date : null,
    evo_end_date: report?.evo_end_date ? report?.evo_end_date : null,
    year_pickup: report?.year_pickup ? report?.year_pickup : null,
    start_week_pickup: report?.start_week_pickup
      ? report?.start_week_pickup
      : null,
    end_week_pickup: report?.end_week_pickup ? report?.end_week_pickup : null,
    original_report: report?.original_report,
    reporte_isotopos: report?.reporte_isotopos.map((item: any) => item.id),
  };
};

export const samplingControlReport = (report: any) => {
  // Cuando viene del formulario, reporte_tipos_analisis son objetos completos
  // Cuando se envía al servidor, deben ser solo IDs
  const analysisTypes = report?.reporte_tipos_analisis || [];
  const mappedAnalysisTypes = Array.isArray(analysisTypes) 
    ? analysisTypes.map((item: any) => typeof item === 'object' && item?.id ? item.id : item)
    : [];

  return {
    id: report?.id,
    report_lid: report?.report_lid === "report_lid",
    type: report?.reportType,
    installation: report?.report_installation?.id,
    reporte_tipos_analisis: mappedAnalysisTypes,
    motivo_muestreo_id: report?.motivo_muestreo_id?.id,
    tipo_muestra_id: report?.tipo_muestra_id?.id,
    punto_muestreo_id: report?.punto_muestreo_id?.id,
    emission_date: report?.emission_date,
    approved_by: report?.approved_by.id,
    evo_start_date: report?.evo_start_date ? report?.evo_start_date : null,
    evo_end_date: report?.evo_end_date ? report?.evo_end_date : null,
    year_pickup: report?.year_pickup ? report?.year_pickup : null,
    start_week_pickup: report?.start_week_pickup
      ? report?.start_week_pickup
      : null,
    end_week_pickup: report?.end_week_pickup ? report?.end_week_pickup : null,
    original_report: report?.original_report,
    comments: report?.comments,
    tritium_water: true,
    // Filtros de actividad
    activity_comparator: report?.activity_comparator || null,
    activity_value: report?.activity_value || null,
    activity_value_max: report?.activity_value_max || null,
    // Filtros de incertidumbre
    uncertainty_comparator: report?.uncertainty_comparator || null,
    uncertainty_value: report?.uncertainty_value || null,
    uncertainty_value_max: report?.uncertainty_value_max || null,
    // Filtros de LID
    lid_comparator: report?.lid_comparator || null,
    lid_value: report?.lid_value || null,
    lid_value_max: report?.lid_value_max || null,
  };
};
export const samplingLIDReport = (report: any) => {
  return {
    id: report?.id,
    installation: report?.installation_lid?.id,
    evo_start_date: dayjs(report?.evo_start_date_lid),
    evo_end_date: dayjs(report?.evo_end_date_lid),
    created_by: report?.created_by?.id,
    emission_date: dayjs(new Date()).format("YYYY-MM-DD"),
    comments: report?.comments,
    approved_by: report?.approved_by.id,
    lid_csn_type: "LID",
    type: "lid_csn",
  };
};

export const samplingAnexosReport = (report: any) => {
  return {
    id: report?.id,
    anexo_1: report?.anexos?.includes("anexo_1"),
    anexo_2: report?.anexos?.includes("anexo_2"),
    anexo_3: report?.anexos?.includes("anexo_3"),
    letter: report?.report_lid === "letter",
    evo_installation: report?.evo_installation?.id,
    start_date: report?.start_date,
    limit_date: report?.limit_date,
    evo_start_date: report?.evo_start_date,
    end_date_origin_year: report?.end_date_origin_year,
    letter_content: report?.report_lid === "letter" && report.letter_content,
    evo_end_date: report?.evo_end_date,
    start_date_origin_year: report?.start_date_origin_year,
  };
};

export const getTypeReportOptions = () => {
  return Object.keys(configButtonsConditioning).map((key) => {
    const { type, title } = configButtonsConditioning[key];
    return {
      id: type,
      label: title,
    };
  });
};

export const DAFAULT_APROVED_BY = {
  id: 2,
  username: "MJDL",
};

export const getDefaultValuesReport = (report?: ServiceReport) => {
  const setAnexos: string[] = [];
  if (report?.anexo_1) setAnexos.push("anexo_1");
  if (report?.anexo_2) setAnexos.push("anexo_2");
  if (report?.anexo_3) setAnexos.push("anexo_3");
  if (report?.letter) setAnexos.push("letter");

  const today = dayjs();

  return {
    id: report?.id || undefined,
    report_lid: !report
      ? "report_lid"
      : report?.report_lid
      ? "report_lid"
      : undefined,
    anexos: setAnexos,
    operation_start_date: report?.operation_start_date
      ? dayjs(report.operation_start_date)
      : undefined,
    operation_limit_date: report?.operation_limit_date
      ? dayjs(report.operation_limit_date)
      : undefined,
    limit_date: report?.limit_date ? dayjs(report.limit_date) : today,
    start_date: report?.start_date ? dayjs(report.start_date) : today,
    letter: report?.letter ? "letter" : undefined,
    letter_content: report?.letter_content || undefined,
    client: report?.client || undefined,
    destinatario_id: report?.destinatario_id || undefined,
    report_installation: report?.installation || undefined,
    approved_by: report?.approved_by || DAFAULT_APROVED_BY,
    emission_date: dayjs(report?.emission_date) || today,
    subtitle: report?.subtitle || undefined,
    masl_reference: report?.masl_reference || undefined,
    client_reference: report?.client_reference || undefined,
    motivo_muestreo_id: report?.motivo_muestreo_id || undefined,
    punto_muestreo_id: report?.punto_muestreo_id || undefined,
    tipo_muestra_id: report?.tipo_muestra_id || undefined,
    shared:
      report?.compartida !== undefined && report?.compartida !== null
        ? report.compartida
          ? yesNoTable[0]
          : yesNoTable[1]
        : undefined,
    acumulacion: typeof report?.acumulacion === 'object' && report?.acumulacion !== null
      ? report.acumulacion
      : report?.acumulacion || undefined,
    end_date_origin_year: dayjs(report?.end_date_origin_year) || today,
    start_date_origin_year: dayjs(report?.start_date_origin_year) || today,
    year_pickup: report?.year_pickup || undefined,
    start_week_pickup: report?.start_week_pickup || undefined,
    end_week_pickup: report?.end_week_pickup || undefined,
    comments: report?.comments || "",
    reportType: report?.type || undefined,
    evo_installation: report?.evo_installation || undefined,
    type: report?.type || undefined,
    evo_start_date: dayjs(report?.evo_start_date) || today,
    evo_end_date: dayjs(report?.evo_end_date) || today,
    searchType: report?.year_pickup ? "dateRangeWeek" : "dateRange",
    created_by: report?.created_by || undefined,
    reporte_tipos_analisis: report?.reporte_tipos_analisis
      ? report.type === "control"
        ? report.reporte_tipos_analisis
        : report.reporte_tipos_analisis.map((item: { id: number }) => item.id)
      : [],
    installation_lid: report?.installation || undefined,
    evo_start_date_lid: dayjs(report?.evo_start_date) || today,
    evo_end_date_lid: dayjs(report?.evo_end_date) || today,
    reporte_isotopos: report?.reporte_isotopos
      ? report.reporte_isotopos.map((item: { id: number; codigo: string }) => {
          return { id: item.id, codigo: item.codigo };
        })
      : [],
    // Filtros de actividad
    activity_comparator: report?.activity_comparator || "",
    activity_value: report?.activity_value || undefined,
    activity_value_max: report?.activity_value_max || undefined,
    // Filtros de incertidumbre
    uncertainty_comparator: report?.uncertainty_comparator || "",
    uncertainty_value: report?.uncertainty_value || undefined,
    uncertainty_value_max: report?.uncertainty_value_max || undefined,
    // Filtros de LID
    lid_comparator: report?.lid_comparator || "",
    lid_value: report?.lid_value || undefined,
    lid_value_max: report?.lid_value_max || undefined,
  };
};

export const adapterAnexos = (
  data: Partial<ReportType>,
  sample: ServiceReport
): PayloadAnexos => {
  const payload: any = {};
  if (!sample?.letter && data.anexos?.includes("letter")) {
    payload.letter = true;
    payload.letter_content = data.letter_content;
  }

  if (!sample?.anexo_1 && data.anexos?.includes("anexo_1")) {
    payload.anexo_1 = true;
  }
  if (data.anexos?.includes("anexo_2")) {
    if (!sample?.anexo_2) {
      payload.anexo_2 = true;
    }
    payload.evo_installation = data.evo_installation?.id;
    payload.operation_start_date = data.operation_start_date;
    payload.operation_limit_date = data.operation_limit_date;
    payload.start_date = data.start_date;
    payload.limit_date = data.limit_date;
  }
  if (data.anexos?.includes("anexo_3")) {
    if (!sample?.anexo_3) {
      payload.anexo_3 = true;
    }
    payload.start_date_origin_year = data.start_date_origin_year;
    payload.end_date_origin_year = data.end_date_origin_year;
  }
  return payload;
};
